import React, { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Form, Button, Tab, Nav, Modal } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./UploadForm.css";
import {
  FaUpload,
  FaFileWord,
  FaFilePdf,
  FaFilePowerpoint,
  FaTimes,
  FaDownload,
  FaPlus,
  FaEye,
  FaCalendarAlt,
  FaUser,
  FaShieldAlt,
  FaCloudUploadAlt,
  FaInfoCircle,
  FaBuilding,
  FaIdBadge
} from "react-icons/fa";
import axios from "axios";

// Main Component
const UploadForm = () => {
  const [activeTab, setActiveTab] = useState("upload");
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    files: [],
  });
  const [userInfo, setUserInfo] = useState({
    userId: "",
    username: "",
    department: "",
    departmentType: ""
  });
  const [isDragging, setIsDragging] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch user info from localStorage on mount
  useEffect(() => {
    const storedUserInfo = {
      userId: localStorage.getItem('userId') || '',
      username: localStorage.getItem('username') || '',
      department: localStorage.getItem('department') || '',
      departmentType: localStorage.getItem('departmentType') || ''
    };

    setUserInfo(storedUserInfo);

    if (!storedUserInfo.userId) {
      setSubmitStatus({
        type: "error",
        message: "User information not found. Please login again."
      });
    } else {
      // Only fetch documents if userId exists
      fetchDocuments(storedUserInfo.userId);
    }
  }, []);

  // Updated fetchDocuments to use the new route: /api/documents/user/:userId
  const fetchDocuments = async (userId) => {
    if (!userId) return;

    try {
      const response = await axios.get(`https://backend.ems.hiradentallab.com/api/documents/user/${userId}`);

      // The controller returns { success: true, count, documents }
      // So we extract the documents array
      setUploadedFiles(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setSubmitStatus({
        type: "error",
        message: "Failed to load your documents. Please try again later."
      });
      setUploadedFiles([]); // Ensure it's empty on error
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processFiles(selectedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  };

  const processFiles = (files) => {
    const validFiles = files.filter((file) => {
      const fileType = file.type;
      const extension = file.name.split(".").pop().toLowerCase();
      return (
        fileType === "application/pdf" ||
        fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        fileType === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
        extension === "pdf" ||
        extension === "docx" ||
        extension === "pptx"
      );
    });

    const newFiles = validFiles.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2) + " MB",
      type: file.type,
      extension: file.name.split(".").pop().toUpperCase(),
      fileObject: file,
    }));

    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, ...newFiles],
    }));
  };

  const removeFile = (id) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((file) => file.id !== id),
    }));
  };

  const getFileIcon = (fileNameOrType) => {
    const name = typeof fileNameOrType === 'string' ? fileNameOrType : '';
    if (name.includes("pdf") || fileNameOrType.includes("pdf")) return <FaFilePdf className="text-danger" />;
    if (name.includes("docx") || fileNameOrType.includes("word")) return <FaFileWord className="text-primary" />;
    if (name.includes("pptx") || fileNameOrType.includes("presentation")) return <FaFilePowerpoint className="text-warning" />;
    return <FaFileWord />;
  };

  const handleDownload = async (id) => {
    try {
      window.location.href = `https://backend.ems.hiradentallab.com/api/documents/${id}/download`;
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await axios.get(`https://backend.ems.hiradentallab.com/api/documents/${id}`);
      setSelectedDocument(response.data.document);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching document details:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await axios.delete(`https://backend.ems.hiradentallab.com/api/documents/${id}`);
        fetchDocuments(userInfo.userId); // Refresh list
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userInfo.userId) {
      setSubmitStatus({
        type: "error",
        message: "User information not found. Please login again."
      });
      return;
    }

    if (!formData.title.trim()) {
      setSubmitStatus({ type: "error", message: "Please enter document title" });
      return;
    }

    if (!formData.date) {
      setSubmitStatus({ type: "error", message: "Please select a date" });
      return;
    }

    if (formData.files.length === 0) {
      setSubmitStatus({ type: "error", message: "Please upload at least one document" });
      return;
    }

    setSubmitStatus({ type: "loading", message: "Uploading your documents..." });

    try {
      for (const file of formData.files) {
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('date', formData.date);
        formDataToSend.append('file', file.fileObject);
        formDataToSend.append('userId', userInfo.userId);
        formDataToSend.append('username', userInfo.username);
        formDataToSend.append('department', userInfo.department);
        formDataToSend.append('departmentType', userInfo.departmentType);

        await axios.post('https://backend.ems.hiradentallab.com/api/documents/upload', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setSubmitStatus({
        type: "success",
        message: `Successfully uploaded ${formData.files.length} document(s)!`,
      });

      // Reset form
      setFormData({ title: "", date: "", files: [] });

      // Refresh documents list
      fetchDocuments(userInfo.userId);

      // Switch to view tab
      setActiveTab("view");
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: 'Upload failed: ' + (error.response?.data?.message || error.message),
      });
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Rest of your JSX remains exactly the same...
  return (
    <div className="upload-page">
      <Container className="form-container">
        {/* Header Section with User Info */}
        <div className="header-section">
          <div className="header-icon-wrapper">
            <FaCloudUploadAlt className="header-main-icon" />
          </div>
          <h1 className="main-title">Document Management System</h1>
          <p className="subtitle">
            Welcome, {userInfo.username || 'User'} - {userInfo.department || 'Department'}
          </p>
          <div className="user-info-badge">
            <div className="user-info-item">
              <FaIdBadge className="me-1" />
              <span>ID: {userInfo.userId || 'N/A'}</span>
            </div>
            <div className="user-info-item">
              <FaBuilding className="me-1" />
              <span>Dept: {userInfo.departmentType || 'N/A'}</span>
            </div>
            <div className="security-badge ms-3">
              <FaShieldAlt className="me-2" />
              <span>256-bit SSL Encryption</span>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="stats-bar mb-4">
          <div className="stat-item">
            <div className="stat-number">{uploadedFiles.length}</div>
            <div className="stat-label">Your Documents</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">
              {uploadedFiles.filter(f => f.originalName?.toLowerCase().endsWith('.pdf')).length}
            </div>
            <div className="stat-label">PDF Files</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">
              {uploadedFiles.filter(f => f.originalName?.toLowerCase().endsWith('.docx')).length}
            </div>
            <div className="stat-label">Word Files</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">
              {uploadedFiles.filter(f => f.originalName?.toLowerCase().endsWith('.pptx')).length}
            </div>
            <div className="stat-label">PowerPoint Files</div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="tabs-navigation mb-4">
          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            <div className="tab-header-strip">
              <Nav variant="pills" className="custom-tabs">
                <Nav.Item>
                  <Nav.Link eventKey="upload" className="tab-link">
                    <div className="tab-link-content">
                      <FaPlus className="tab-icon" />
                      <span>Upload Documents</span>
                      {formData.files.length > 0 && (
                        <span className="tab-badge">{formData.files.length}</span>
                      )}
                    </div>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="view" className="tab-link">
                    <div className="tab-link-content">
                      <FaEye className="tab-icon" />
                      <span>View Documents</span>
                      {uploadedFiles.length > 0 && (
                        <span className="tab-badge">{uploadedFiles.length}</span>
                      )}
                    </div>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>

            <Tab.Content>
              {/* UPLOAD TAB */}
              <Tab.Pane eventKey="upload">
                <div className="form-card">
                  <div className="form-card-header">
                    <h4>Upload New Documents</h4>
                    <p className="form-card-subtitle">
                      Upload documents as {userInfo.username} ({userInfo.department})
                    </p>
                  </div>
                  <Form onSubmit={handleSubmit} className="upload-form mt-2">
                    <Row>
                      <Col md={12} lg={6}>
                        <Form.Group className="mb-4 input-group-custom">
                          <div className="input-icon-wrapper">
                            <FaFileWord className="input-icon" />
                          </div>
                          <div className="input-content">
                            <Form.Label className="form-label">
                              Document Title <span className="required-star">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="title"
                              value={formData.title}
                              onChange={handleInputChange}
                              placeholder="Enter document title"
                              className="form-control-custom"
                              required
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={12} lg={6}>
                        <Form.Group className="mb-4 input-group-custom">
                          <div className="input-icon-wrapper">
                            <FaCalendarAlt className="input-icon" />
                          </div>
                          <div className="input-content">
                            <Form.Label className="form-label">
                              Select Date <span className="required-star">*</span>
                            </Form.Label>
                            <Form.Control
                              type="date"
                              name="date"
                              value={formData.date}
                              onChange={handleInputChange}
                              className="form-control-custom"
                              required
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <div className="upload-section">
                          <div className="section-header">
                            <Form.Label className="form-label">
                              Upload Documents <span className="required-star">*</span>
                            </Form.Label>
                            <span className="file-count">
                              {formData.files.length} file{formData.files.length !== 1 ? 's' : ''} selected
                            </span>
                          </div>
                          <div
                            className={`upload-dropzone ${isDragging ? "dragging" : ""}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={triggerFileInput}
                          >
                            <div className="dropzone-content">
                              <div className="dropzone-icon-wrapper">
                                <FaUpload className="upload-icon" />
                              </div>
                              <p className="dropzone-text">
                                <strong>Drag & Drop files here</strong>
                              </p>
                              <p className="dropzone-subtext">
                                or click to browse from your device
                              </p>
                              <div className="file-types">
                                <span className="file-type-badge">PDF</span>
                                <span className="file-type-badge">DOCX</span>
                                <span className="file-type-badge">PPTX</span>
                              </div>
                              <p className="dropzone-note">
                                Maximum file size: 10MB per file
                              </p>
                            </div>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileSelect}
                              accept=".pdf,.docx,.pptx"
                              multiple
                              className="file-input"
                            />
                          </div>

                          {formData.files.length > 0 && (
                            <div className="file-list mt-4">
                              <div className="file-list-header">
                                <p className="file-list-title">Selected Files</p>
                                <button
                                  type="button"
                                  className="clear-all-btn"
                                  onClick={() => setFormData(prev => ({ ...prev, files: [] }))}
                                >
                                  Clear All
                                </button>
                              </div>
                              <div className="file-items">
                                {formData.files.map((file) => (
                                  <div key={file.id} className="file-item">
                                    <div className="file-icon">
                                      {getFileIcon(file.type)}
                                    </div>
                                    <div className="file-details">
                                      <span className="file-name">{file.name}</span>
                                      <span className="file-meta">{file.size} • {file.extension}</span>
                                    </div>
                                    <div className="file-actions">
                                      <button
                                        type="button"
                                        className="remove-file-btn"
                                        onClick={() => removeFile(file.id)}
                                        title="Remove file"
                                      >
                                        <FaTimes />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </Col>

                      <Col md={12}>
                        <div className="submit-section">
                          <Button
                            type="submit"
                            className="submit-btn"
                            disabled={
                              !formData.title ||
                              !formData.date ||
                              formData.files.length === 0 ||
                              !userInfo.userId
                            }
                          >
                            <FaCloudUploadAlt className="me-2" />
                            Upload {formData.files.length > 0 ? `${formData.files.length} Document${formData.files.length !== 1 ? 's' : ''}` : 'Documents'}
                          </Button>

                          {submitStatus && (
                            <div className={`alert alert-${submitStatus.type === "success" ? "success" : submitStatus.type === "loading" ? "info" : "danger"} mt-3`}>
                              <div className="alert-content">
                                {submitStatus.type === "loading" && (
                                  <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                )}
                                {submitStatus.message}
                              </div>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </div>
              </Tab.Pane>

              {/* VIEW TAB */}
              <Tab.Pane eventKey="view">
                <div className="form-card">
                  <div className="form-card-header">
                    <h4>Your Documents</h4>
                    <p className="form-card-subtitle">
                      {uploadedFiles.length} document{uploadedFiles.length !== 1 ? 's' : ''} uploaded by you
                    </p>
                  </div>
                  <div className="view-documents mt-2">
                    {uploadedFiles.length === 0 ? (
                      <div className="no-files text-center py-5">
                        <div className="no-files-icon-wrapper">
                          <FaEye className="no-files-icon mb-3" />
                        </div>
                        <h4>No documents uploaded yet</h4>
                        <p className="text-muted">Start by uploading your first document</p>
                        <Button
                          variant="primary"
                          className="mt-3 upload-first-btn"
                          onClick={() => setActiveTab("upload")}
                        >
                          <FaPlus className="me-2" />
                          Upload Your First Document
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="document-actions mb-4">
                          <div className="search-box">
                            <input type="text" placeholder="Search documents..." className="search-input" />
                          </div>
                          <div className="filter-buttons">
                            <Button variant="outline-secondary" size="sm">All</Button>
                            <Button variant="outline-secondary" size="sm">PDF</Button>
                            <Button variant="outline-secondary" size="sm">Word</Button>
                            <Button variant="outline-secondary" size="sm">PowerPoint</Button>
                          </div>
                        </div>
                        <div className="files-grid">
                          <Row>
                            {uploadedFiles.map((file) => (
                              <Col md={6} lg={4} key={file._id} className="mb-4">
                                <div className="file-card">
                                  <div className="file-card-header">
                                    <div className="file-icon-large">
                                      {getFileIcon(file.originalName)}
                                    </div>
                                    <span className="file-extension">
                                      {file.originalName.split('.').pop().toUpperCase()}
                                    </span>
                                    <div className="file-card-badge">
                                      {new Date(file.date).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <div className="file-card-body">
                                    <h6 className="file-card-title">{file.title}</h6>
                                    <div className="file-card-details">
                                      <div className="file-meta-item">
                                        <span className="meta-label">Uploaded by:</span>
                                        <span className="meta-value">{file.username}</span>
                                      </div>
                                      <div className="file-meta-item">
                                        <span className="meta-label">Department:</span>
                                        <span className="meta-value">{file.department}</span>
                                      </div>
                                      <div className="file-meta-item">
                                        <span className="meta-label">Date:</span>
                                        <span className="meta-value">{new Date(file.date).toLocaleDateString()}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="file-card-footer">
                                    <Button
                                      variant="success"
                                      size="sm"
                                      className="download-btn"
                                      onClick={() => handleDownload(file._id)}
                                    >
                                      <FaDownload className="me-2" />
                                      Download
                                    </Button>
                                    <Button
                                      variant="info"
                                      size="sm"
                                      className="view-btn ms-1"
                                      onClick={() => handleViewDetails(file._id)}
                                    >
                                      <FaInfoCircle className="me-1" />
                                      Details
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      className="delete-btn ms-1"
                                      onClick={() => handleDelete(file._id)}
                                    >
                                      <FaTimes className="me-1" />
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>

        {/* Footer Note */}
        <div className="footer-note mt-4">
          <div className="footer-icon">
            <FaShieldAlt />
          </div>
          <p className="note-text">
            <strong>Secure Storage:</strong> Your documents are encrypted using 256-bit SSL encryption and securely stored. We never share your information with third parties.
          </p>
        </div>

        {/* Modal for Document Details */}
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Document Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedDocument ? (
              <div>
                <p><strong>Title:</strong> {selectedDocument.title}</p>
                <p><strong>Date:</strong> {new Date(selectedDocument.date).toLocaleDateString()}</p>
                <p><strong>File Name:</strong> {selectedDocument.originalName}</p>
                <p><strong>Uploaded By:</strong> {selectedDocument.username}</p>
                <p><strong>User ID:</strong> {selectedDocument.userId}</p>
                <p><strong>Department:</strong> {selectedDocument.department}</p>
                <p><strong>Department Type:</strong> {selectedDocument.departmentType}</p>
                <p><strong>Uploaded At:</strong> {new Date(selectedDocument.createdAt).toLocaleString()}</p>
              </div>
            ) : (
              <p>Loading details...</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default UploadForm;